import React, { useState, useEffect } from "react";
import { Card, Typography, Space, Tag, Divider, Alert, Descriptions, Input, Button, List, Collapse, Checkbox, Badge, Tooltip, Row, Col } from "antd";
import { CheckOutlined, EditOutlined, InfoCircleOutlined, MailOutlined, DatabaseOutlined, SettingOutlined, CheckCircleOutlined, ClockCircleOutlined, ShoppingCartOutlined, UserOutlined, CalendarOutlined, EnvironmentOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import ExecutionResultsViewer from "./ExecutionResultsViewer";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

/**
 * V5.0 操作列表审阅组件
 * 支持部分批准、操作状态显示和只读模式，标准化处理器接口
 * @param {Object} props.actionItem - Action Item 数据
 * @param {Function} props.onConfirm - 确认回调
 * @param {Function} props.onReject - 拒绝回调
 * @param {boolean} props.loading - 是否正在处理
 * @param {boolean} props.readOnly - 是否为只读模式
 */
const OperationListReview = ({ actionItem, onConfirm, onReject, loading = false, readOnly = false }) => {
    const [modifiedOperations, setModifiedOperations] = useState({});
    const [selectedOperations, setSelectedOperations] = useState(new Set());
    const [isPartialMode, setIsPartialMode] = useState(false);

    const { suggested_payload, user_modified_payload } = actionItem;
    const { system_operations = [], user_interaction = {}, reasoning } = suggested_payload;
    const { context_data_summary, business_context } = suggested_payload;

    // 获取已批准的操作ID集合
    const getApprovedOperationIds = () => {
        if (!user_modified_payload?.system_operations) return new Set();
        return new Set(
            user_modified_payload.system_operations
                .filter((op) => op.op_meta?.decision === "APPROVED")
                .map((op) => op.operation_id)
                .filter(Boolean),
        );
    };

    // 获取已拒绝的操作ID集合
    const getRejectedOperationIds = () => {
        if (!user_modified_payload?.system_operations) return new Set();
        return new Set(
            user_modified_payload.system_operations
                .filter((op) => op.op_meta?.decision === "REJECTED")
                .map((op) => op.operation_id)
                .filter(Boolean),
        );
    };

    // 获取操作的执行状态
    const getOperationExecutionStatus = (operationId) => {
        if (!user_modified_payload?.system_operations) return null;
        const operation = user_modified_payload.system_operations.find((op) => op.operation_id === operationId);
        if (!operation?.op_meta?.execution_history) return null;

        const latestExecution = operation.op_meta.execution_history[operation.op_meta.execution_history.length - 1];
        return latestExecution;
    };

    const approvedOperationIds = getApprovedOperationIds();
    const rejectedOperationIds = getRejectedOperationIds();
    const hasApprovedOperations = approvedOperationIds.size > 0;
    const hasRejectedOperations = rejectedOperationIds.size > 0;

    // 获取剩余未处理的操作
    const getRemainingOperations = () => {
        return system_operations.filter((op) => {
            const operationId = op.operation_id;
            if (!operationId) return false;

            // 已拒绝的操作不显示
            if (rejectedOperationIds.has(operationId)) return false;

            // 未批准的操作显示
            if (!approvedOperationIds.has(operationId)) return true;

            // 已批准的操作：检查执行状态
            const executionStatus = getOperationExecutionStatus(operationId);
            if (!executionStatus) {
                // 已批准但未执行的操作不显示（待执行）
                return false;
            }

            // 已批准且执行失败的操作需要重新显示给用户决策
            return !executionStatus.success;
        });
    };

    const remainingOperations = getRemainingOperations();

    // 渲染操作状态标识
    const renderOperationStatusIcon = (operation) => {
        const operationId = operation.operation_id;
        const isApproved = approvedOperationIds.has(operationId);
        const isRejected = rejectedOperationIds.has(operationId);
        const executionStatus = getOperationExecutionStatus(operationId);

        if (isRejected) {
            return (
                <Tooltip title="已拒绝">
                    <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                </Tooltip>
            );
        }

        if (isApproved) {
            if (executionStatus) {
                if (executionStatus.success) {
                    return (
                        <Tooltip title={`已执行成功 - ${executionStatus.timestamp}`}>
                            <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                        </Tooltip>
                    );
                } else {
                    return (
                        <Tooltip title={`执行失败 - ${executionStatus.message || "未知错误"}`}>
                            <ExclamationCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                        </Tooltip>
                    );
                }
            } else {
                return (
                    <Tooltip title="已批准，待执行">
                        <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                    </Tooltip>
                );
            }
        }

        if (!readOnly) {
            const isSelected = selectedOperations.has(operationId);
            if (isSelected) {
                return (
                    <Tooltip title="待批准">
                        <ClockCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
                    </Tooltip>
                );
            }
        }

        return null;
    };

    // 初始化选择状态
    useEffect(() => {
        if (!readOnly && !isPartialMode) {
            // 非部分模式：选择所有剩余操作
            const remainingIds = new Set(remainingOperations.map((op) => op.operation_id).filter(Boolean));
            setSelectedOperations(remainingIds);
        }
    }, [isPartialMode, remainingOperations.length, readOnly]);

    // 获取操作类型图标
    const getOperationIcon = (operationType) => {
        switch (operationType) {
            case "CREATE_RECORD":
            case "UPDATE_RECORD":
            case "DELETE_RECORD":
                return <DatabaseOutlined style={{ color: "#1890ff" }} />;
            case "REPLY_EMAIL":
            case "SEND_EMAIL":
                return <MailOutlined style={{ color: "#52c41a" }} />;
            case "ANALYSIS_ONLY":
                return <InfoCircleOutlined style={{ color: "#fa8c16" }} />;
            default:
                return <SettingOutlined style={{ color: "#722ed1" }} />;
        }
    };

    // 获取操作类型标签颜色
    const getOperationTagColor = (operationType) => {
        switch (operationType) {
            case "CREATE_RECORD":
                return "green";
            case "UPDATE_RECORD":
                return "blue";
            case "DELETE_RECORD":
                return "red";
            case "REPLY_EMAIL":
            case "SEND_EMAIL":
                return "orange";
            case "ANALYSIS_ONLY":
                return "gold";
            default:
                return "purple";
        }
    };

    // 渲染操作参数摘要
    const renderOperationSummary = (operation, index) => {
        const { operation_type, target_service, operation_parameters = {} } = operation;

        let summary = "";
        switch (operation_type) {
            case "CREATE_RECORD":
                if (target_service === "CateringOrderService") {
                    summary = `创建航食订单 - 航班ID: ${operation_parameters.flight_id}, 供应商ID: ${operation_parameters.supplier_id}`;
                } else {
                    summary = `创建记录到 ${target_service}`;
                }
                break;
            case "UPDATE_RECORD":
                if (target_service === "CateringOrderService") {
                    const recordId = operation_parameters.record_id;
                    const updateFields = operation_parameters.update_fields || {};
                    const status = updateFields.status;
                    const remarks = updateFields.remarks;
                    summary = `更新航食订单 #${recordId}${status ? ` - 状态: ${status}` : ""}${remarks ? ` - 备注: ${remarks.substring(0, 50)}${remarks.length > 50 ? "..." : ""}` : ""}`;
                } else {
                    summary = `更新 ${target_service} 记录`;
                }
                break;
            case "REPLY_EMAIL":
                summary = `回复邮件给 ${operation_parameters.recipient} - 主题: ${operation_parameters.subject}`;
                break;
            case "SEND_EMAIL":
                summary = `发送邮件给 ${operation_parameters.recipient} - 主题: ${operation_parameters.subject}`;
                break;
            case "ANALYSIS_ONLY":
                const analysisSum = operation_parameters.summary;
                summary = `分析建议: ${analysisSum ? analysisSum.substring(0, 80) + (analysisSum.length > 80 ? "..." : "") : "查看详细分析"}`;
                break;
            default:
                summary = `执行 ${operation_type} 操作`;
        }

        return summary;
    };

    // 渲染操作的详细信息
    const renderOperationDetails = (operation, index) => {
        const { operation_type, target_service, operation_parameters = {} } = operation;

        switch (operation_type) {
            case "UPDATE_RECORD":
                if (target_service === "CateringOrderService") {
                    const updateFields = operation_parameters.update_fields || {};
                    return (
                        <Card size="small" style={{ marginTop: 8, backgroundColor: "#f6ffed" }}>
                            <Descriptions size="small" column={2}>
                                <Descriptions.Item label="记录ID">#{operation_parameters.record_id}</Descriptions.Item>
                                {updateFields.status && <Descriptions.Item label="新状态">{updateFields.status}</Descriptions.Item>}
                                {updateFields.remarks && (
                                    <Descriptions.Item label="备注" span={2}>
                                        <div style={{ maxHeight: "60px", overflowY: "auto", fontSize: "12px" }}>{updateFields.remarks}</div>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>
                    );
                }
                break;
            case "ANALYSIS_ONLY":
                const summary = operation_parameters.summary;
                if (summary) {
                    return (
                        <Card size="small" style={{ marginTop: 8, backgroundColor: "#fff7e6" }}>
                            <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                <Text strong style={{ color: "#fa8c16" }}>
                                    分析建议:
                                </Text>
                                <div style={{ marginTop: 4, maxHeight: "80px", overflowY: "auto" }}>{summary}</div>
                            </div>
                        </Card>
                    );
                }
                break;
        }
        return null;
    };

    // 渲染业务上下文信息
    const renderBusinessContext = () => {
        if (!business_context) return null;

        const { email_info, business_scenario, involved_flights, business_transactions } = business_context;

        return (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: "#f0f9ff" }}>
                <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ color: "#1890ff" }}>
                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                        业务上下文信息
                    </Text>
                </div>

                <Row gutter={16}>
                    {/* 邮件信息 */}
                    {email_info && (
                        <Col span={12}>
                            <Card size="small" style={{ height: "100%" }}>
                                <Text strong style={{ fontSize: "12px" }}>
                                    <MailOutlined style={{ marginRight: 4 }} />
                                    邮件信息
                                </Text>
                                <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                                    <Descriptions.Item label="发件人">{email_info.from_address}</Descriptions.Item>
                                    <Descriptions.Item label="主题">{email_info.subject}</Descriptions.Item>
                                    <Descriptions.Item label="时间">{new Date(email_info.date).toLocaleString()}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Col>
                    )}

                    {/* 业务场景 */}
                    {business_scenario && (
                        <Col span={12}>
                            <Card size="small" style={{ height: "100%" }}>
                                <Text strong style={{ fontSize: "12px" }}>
                                    <SettingOutlined style={{ marginRight: 4 }} />
                                    业务场景
                                </Text>
                                <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                                    <Descriptions.Item label="业务类型">{business_scenario.primary_business_type}</Descriptions.Item>
                                    <Descriptions.Item label="业务范围">{business_scenario.business_scope}</Descriptions.Item>
                                    <Descriptions.Item label="摘要">{business_scenario.summary}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Col>
                    )}
                </Row>

                {/* 涉及航班 */}
                {involved_flights && involved_flights.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <Text strong style={{ fontSize: "12px" }}>
                            <EnvironmentOutlined style={{ marginRight: 4 }} />
                            涉及航班 ({involved_flights.length}个)
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {involved_flights.map((flight, index) => (
                                <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                                    {flight}
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}

                {/* 业务交易信息 */}
                {business_transactions && business_transactions.catering_service && (
                    <div style={{ marginTop: 12 }}>
                        <Text strong style={{ fontSize: "12px" }}>
                            <ShoppingCartOutlined style={{ marginRight: 4 }} />
                            航食订单信息
                        </Text>
                        <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>{business_transactions.catering_service.summary}</div>
                        {business_transactions.catering_service.flight_order_mapping && (
                            <div style={{ marginTop: 8 }}>
                                {Object.entries(business_transactions.catering_service.flight_order_mapping).map(([flightId, orders]) => (
                                    <div key={flightId} style={{ marginBottom: 8 }}>
                                        {orders.map((order, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#666",
                                                    backgroundColor: "#fafafa",
                                                    padding: "4px 8px",
                                                    borderRadius: "4px",
                                                    marginBottom: "4px",
                                                }}
                                            >
                                                {order}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Card>
        );
    };

    // 渲染邮件操作的详细内容
    const renderEmailDetails = (operation, index) => {
        const { operation_parameters = {} } = operation;
        const { recipient, subject, body_suggestion } = operation_parameters;

        if (!recipient && !subject && !body_suggestion) return null;

        return (
            <Card size="small" style={{ marginTop: 8, backgroundColor: "#fafafa" }}>
                <Descriptions size="small" column={1}>
                    {recipient && <Descriptions.Item label="收件人">{recipient}</Descriptions.Item>}
                    {subject && <Descriptions.Item label="主题">{subject}</Descriptions.Item>}
                </Descriptions>
                {body_suggestion && (
                    <div style={{ marginTop: 8 }}>
                        <Text strong>邮件内容:</Text>
                        {readOnly ? (
                            <div
                                style={{
                                    marginTop: 4,
                                    padding: "8px",
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    maxHeight: "120px",
                                    overflowY: "auto",
                                }}
                            >
                                {body_suggestion}
                            </div>
                        ) : (
                            <TextArea
                                value={modifiedOperations[index]?.body_suggestion || body_suggestion}
                                onChange={(e) => {
                                    setModifiedOperations((prev) => ({
                                        ...prev,
                                        [index]: {
                                            ...operation,
                                            operation_parameters: {
                                                ...operation_parameters,
                                                body_suggestion: e.target.value,
                                            },
                                        },
                                    }));
                                }}
                                rows={6}
                                style={{ marginTop: 4 }}
                                placeholder="邮件内容"
                            />
                        )}
                    </div>
                )}
            </Card>
        );
    };

    // 处理操作选择
    const handleOperationSelect = (operationId, checked) => {
        setSelectedOperations((prev) => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(operationId);
            } else {
                newSet.delete(operationId);
            }
            return newSet;
        });
    };

    // 处理全选/取消全选
    const handleSelectAll = (checked) => {
        if (checked) {
            const allRemainingIds = new Set(remainingOperations.map((op) => op.operation_id).filter(Boolean));
            setSelectedOperations(allRemainingIds);
        } else {
            setSelectedOperations(new Set());
        }
    };

    // 处理确认
    const handleConfirm = () => {
        const hasModifications = Object.keys(modifiedOperations).length > 0;
        const selectedOps = remainingOperations.filter((op) => selectedOperations.has(op.operation_id));

        // 应用修改到选中的操作
        const finalOperations = selectedOps.map((op, originalIndex) => {
            const modifiedOp = modifiedOperations[originalIndex];
            if (modifiedOp) {
                return {
                    ...modifiedOp,
                    op_meta: {
                        decision: "APPROVED",
                        edited: true,
                        approved_by: "current_user",
                        approved_at: new Date().toISOString(),
                    },
                };
            }
            return {
                ...op,
                op_meta: {
                    decision: "APPROVED",
                    edited: false,
                    approved_by: "current_user",
                    approved_at: new Date().toISOString(),
                },
            };
        });

        // 构建部分审批的 payload
        const partialPayload = {
            ...suggested_payload,
            reasoning: hasApprovedOperations ? `继续审批：批准${finalOperations.length}个操作` : selectedOps.length < remainingOperations.length ? `部分审批：批准${finalOperations.length}个操作，剩余${remainingOperations.length - selectedOps.length}个操作待审批` : reasoning,
            system_operations: finalOperations,
        };

        const isPartialApproval = selectedOps.length < remainingOperations.length || hasApprovedOperations;
        const remarks = isPartialApproval ? `部分审批：批准${selectedOps.length}个操作${hasModifications ? "（含修改）" : ""}` : `全部确认：批准${selectedOps.length}个操作${hasModifications ? "（含修改）" : ""}`;

        onConfirm({
            action_taken: "USER_MODIFIED_CONFIRMED",
            user_modified_payload: partialPayload,
            user_remarks: remarks,
        });
    };

    // 处理拒绝
    const handleReject = () => {
        // 如果是部分模式且有选择操作，则进行部分拒绝
        if (isPartialMode && selectedCount > 0) {
            const selectedOps = remainingOperations.filter((op) => selectedOperations.has(op.operation_id));
            
            // 构建部分拒绝的 payload
            const partialRejectPayload = {
                ...suggested_payload,
                system_operations: selectedOps.map((op) => ({
                    ...op,
                    op_meta: {
                        decision: "REJECTED",
                        rejected_by: "current_user",
                        rejected_at: new Date().toISOString(),
                        rejection_reason: "用户拒绝执行操作",
                    },
                })),
            };

            // 调用部分拒绝API（复用modify接口）
            onConfirm({
                action_taken: "USER_PARTIAL_REJECT",
                user_modified_payload: partialRejectPayload,
                user_remarks: `用户部分拒绝 ${selectedCount} 个操作`,
            });
        } else {
            // 全部拒绝
            onReject({
                action_taken: "USER_REJECTED",
                user_remarks: "用户拒绝执行操作",
            });
        }
    };

    const hasModifications = Object.keys(modifiedOperations).length > 0;
    const selectedCount = selectedOperations.size;
    const remainingCount = remainingOperations.length;
    const isAllSelected = selectedCount === remainingCount && remainingCount > 0;
    const isIndeterminate = selectedCount > 0 && selectedCount < remainingCount;

    // V4.1 新增：只读模式渲染
    if (readOnly) {
        return (
            <div>
                {/* 业务上下文信息 */}
                {renderBusinessContext()}

                {/* AI 推理说明 */}
                {reasoning && <Alert message="AI 分析与建议" description={reasoning} type="info" showIcon style={{ marginBottom: 16 }} />}

                {/* 显示操作执行摘要 */}
                <Card
                    title={
                        <Space>
                            <InfoCircleOutlined />
                            <span>操作执行摘要 ({system_operations.length}个操作)</span>
                        </Space>
                    }
                    size="small"
                    style={{ marginBottom: 16 }}
                >
                    <List
                        dataSource={system_operations}
                        renderItem={(operation, index) => {
                            const isApproved = approvedOperationIds.has(operation.operation_id);
                            const isRejected = rejectedOperationIds.has(operation.operation_id);

                            return (
                                <List.Item
                                    style={{
                                        backgroundColor: isApproved ? "#f6ffed" : isRejected ? "#fff2f0" : "#fafafa",
                                        border: "1px solid #f0f0f0",
                                        borderRadius: "4px",
                                        marginBottom: "8px",
                                        padding: "12px",
                                    }}
                                >
                                    <div style={{ width: "100%" }}>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                            {/* 状态标识 */}
                                            {renderOperationStatusIcon(operation)}

                                            {getOperationIcon(operation.operation_type)}
                                            <Tag color={getOperationTagColor(operation.operation_type)} style={{ marginLeft: 8 }}>
                                                {operation.operation_type}
                                            </Tag>
                                            {operation.target_service && <Tag color="default">{operation.target_service}</Tag>}

                                            {/* 操作ID */}
                                            {operation.operation_id && (
                                                <Tag color="geekblue" style={{ marginLeft: "auto" }}>
                                                    {operation.operation_id}
                                                </Tag>
                                            )}
                                        </div>

                                        <Text>{renderOperationSummary(operation, index)}</Text>

                                        {/* 操作详情 */}
                                        {renderOperationDetails(operation, index)}
                                        {operation.operation_type.includes("EMAIL") && renderEmailDetails(operation, index)}
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                </Card>
            </div>
        );
    }

    return (
        <div className="operation-list-review">
            {/* 业务上下文信息 */}
            {renderBusinessContext()}

            {/* AI 推理说明 */}
            {reasoning && <Alert message="AI 分析与建议" description={reasoning} type="info" showIcon style={{ marginBottom: 16 }} />}

            {/* 执行结果显示 - V3.0支持 */}
            {actionItem.additional_info?.execution_results && (
                <Card
                    title={
                        <Space>
                            <CheckCircleOutlined />
                            <span>执行结果</span>
                            <Tag color="green">{actionItem.additional_info.execution_results.length} 个操作结果</Tag>
                        </Space>
                    }
                    size="small"
                    style={{ marginBottom: 16 }}
                >
                    <ExecutionResultsViewer 
                        executionResults={actionItem.additional_info.execution_results} 
                        showDetails={true} 
                    />
                </Card>
            )}

            {/* 已处理操作提示 */}
            {(hasApprovedOperations || hasRejectedOperations) && (
                <Alert
                    message={`已处理 ${approvedOperationIds.size + rejectedOperationIds.size} 个操作`}
                    description={
                        <div>
                            {hasApprovedOperations && <span>已批准: {approvedOperationIds.size}个</span>}
                            {hasApprovedOperations && hasRejectedOperations && <span> | </span>}
                            {hasRejectedOperations && <span>已拒绝: {rejectedOperationIds.size}个</span>}
                            {remainingCount > 0 && <span> | 剩余: {remainingCount}个待审批</span>}
                        </div>
                    }
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* 操作模式切换 */}
            {remainingCount > 1 && (
                <Card size="small" style={{ marginBottom: 16 }}>
                    <Space>
                        <Checkbox checked={!isPartialMode} onChange={(e) => setIsPartialMode(!e.target.checked)}>
                            批量操作模式
                        </Checkbox>
                        <Checkbox checked={isPartialMode} onChange={(e) => setIsPartialMode(e.target.checked)}>
                            逐项选择模式
                        </Checkbox>
                    </Space>
                </Card>
            )}

            {/* 操作列表 */}
            <Card
                title={
                    <Space>
                        <InfoCircleOutlined />
                        <span>
                            {hasApprovedOperations ? "剩余待审批操作" : "建议执行的操作"}({remainingCount}个)
                        </span>
                        {isPartialMode && remainingCount > 1 && (
                            <Checkbox indeterminate={isIndeterminate} checked={isAllSelected} onChange={(e) => handleSelectAll(e.target.checked)} style={{ marginLeft: 16 }}>
                                全选
                            </Checkbox>
                        )}
                    </Space>
                }
                size="small"
                style={{ marginBottom: 16 }}
            >
                <List
                    dataSource={remainingOperations}
                    renderItem={(operation, index) => {
                        const isSelected = selectedOperations.has(operation.operation_id);
                        const isApproved = approvedOperationIds.has(operation.operation_id);
                        const isRejected = rejectedOperationIds.has(operation.operation_id);

                        return (
                            <List.Item
                                key={operation.operation_id || index}
                                style={{
                                    opacity: isApproved || isRejected ? 0.6 : 1,
                                    backgroundColor: isSelected ? "#f6ffed" : isRejected ? "#fff2f0" : "transparent",
                                    border: isSelected ? "1px solid #b7eb8f" : isRejected ? "1px solid #ffccc7" : "1px solid transparent",
                                    borderRadius: 4,
                                    padding: 12,
                                }}
                            >
                                <div style={{ width: "100%" }}>
                                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                        {/* 选择框（部分模式） */}
                                        {isPartialMode && !isApproved && !isRejected && <Checkbox checked={isSelected} onChange={(e) => handleOperationSelect(operation.operation_id, e.target.checked)} style={{ marginRight: 12 }} />}

                                        {/* 状态标识 */}
                                        {renderOperationStatusIcon(operation)}

                                        {getOperationIcon(operation.operation_type)}
                                        <Tag color={getOperationTagColor(operation.operation_type)} style={{ marginLeft: 8 }}>
                                            {operation.operation_type}
                                        </Tag>
                                        {operation.target_service && <Tag color="default">{operation.target_service}</Tag>}

                                        {/* 操作ID */}
                                        {operation.operation_id && (
                                            <Tag color="geekblue" style={{ marginLeft: "auto" }}>
                                                {operation.operation_id}
                                            </Tag>
                                        )}
                                    </div>

                                    <Text>{renderOperationSummary(operation, index)}</Text>

                                    {/* 操作详细信息 */}
                                    {renderOperationDetails(operation, index)}

                                    {/* 邮件操作的详细内容 */}
                                    {(operation.operation_type === "REPLY_EMAIL" || operation.operation_type === "SEND_EMAIL") && renderEmailDetails(operation, index)}
                                </div>
                            </List.Item>
                        );
                    }}
                />
            </Card>

            {/* 操作按钮 */}
            <div style={{ textAlign: "center", marginTop: 16 }}>
                <Space size="large">
                    <Button type="primary" icon={<CheckOutlined />} size="large" loading={loading} onClick={handleConfirm} disabled={selectedCount === 0} style={{ minWidth: 140 }}>
                        {selectedCount === 0 ? "请选择操作" : selectedCount < remainingCount ? `部分批准 (${selectedCount}/${remainingCount})` : hasModifications ? "确认修改并执行" : "确认执行"}
                    </Button>

                    <Button danger size="large" loading={loading} onClick={handleReject} style={{ minWidth: 120 }}>
                        {isPartialMode && selectedCount > 0 
                            ? selectedCount < remainingCount 
                                ? `部分拒绝 (${selectedCount}/${remainingCount})`
                                : `全部拒绝 (${selectedCount}/${remainingCount})`
                            : "拒绝"
                        }
                    </Button>
                </Space>

                {/* 操作统计 */}
                {(hasApprovedOperations || selectedCount > 0) && (
                    <div style={{ marginTop: 12, color: "#666", fontSize: "12px" }}>
                        {hasApprovedOperations && <span>已批准: {approvedOperationIds.size}个 | </span>}
                        <span>本次选择: {selectedCount}个</span>
                        {remainingCount > selectedCount && <span> | 剩余: {remainingCount - selectedCount}个</span>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OperationListReview;
